import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSetAtom, useAtomValue } from 'jotai';
import { atom } from 'jotai';
import { useCallback } from 'react';
import { QUERY_KEYS, useAnalytics } from '@restworld/utils-common';
import { useJobPositionsService, useEmployersService } from '@restworld/data-services';
import { useTranslation } from 'next-i18next';
import { showToast } from '@restworld/ui-ds';
import { useAuth } from '@restworld/feature-authentication';
import {
  useIsSubscriptionFeedbacksSubmitted,
  useFetchIsSubscriptionFeedbacksSubmittedQuery,
} from '../../flexible-slots-v2/flexible-slots-v2.hooks';

export const putOfflineJobPositionConfirmationModalAtom = atom<boolean>(false);
export const putOfflineJobPositionNPSModalAtom = atom<boolean>(false);
export const putOfflineJobPositionSuccessModalAtom = atom<boolean>(false);
export const putOfflineJobPositionRecoveryModalAtom = atom<boolean>(false);
export const putOfflineJobPositionReferralModalAtom = atom<boolean>(false);
export const jobPositionIdToPutOfflineAtom = atom<string | null>('');

// NPS score bands: 1-6 → Recovery, 7-8 → standard success, 9-10 → Referral
const NPS_MOTIVE_MAX = 6;
const NPS_REFERRAL_MIN = 9;

export interface NPSFeedbackData {
  npsScore: number;
  motiveForCancelling?: string;
  additionalFeedback?: string;
  reactivationDate?: string;
}

const NPS_OFFLINE_DELAY_SECONDS = 60;

export function usePutOfflineJobPosition() {
  const queryClient = useQueryClient();
  const jobPositionsService = useJobPositionsService();
  const employersService = useEmployersService();
  const { t } = useTranslation('job-positions-page');
  const { user } = useAuth();
  const { trackEvent } = useAnalytics();

  const setIsConfirmationModalOpen = useSetAtom(putOfflineJobPositionConfirmationModalAtom);
  const setIsNPSModalOpen = useSetAtom(putOfflineJobPositionNPSModalAtom);
  const setIsSuccessModalOpen = useSetAtom(putOfflineJobPositionSuccessModalAtom);
  const setIsRecoveryModalOpen = useSetAtom(putOfflineJobPositionRecoveryModalAtom);
  const setIsReferralModalOpen = useSetAtom(putOfflineJobPositionReferralModalAtom);
  const setJpIdToPutOffline = useSetAtom(jobPositionIdToPutOfflineAtom);
  const isConfirmationModalOpen = useAtomValue(putOfflineJobPositionConfirmationModalAtom);
  const isNPSModalOpen = useAtomValue(putOfflineJobPositionNPSModalAtom);
  const isSuccessModalOpen = useAtomValue(putOfflineJobPositionSuccessModalAtom);
  const isRecoveryModalOpen = useAtomValue(putOfflineJobPositionRecoveryModalAtom);
  const isReferralModalOpen = useAtomValue(putOfflineJobPositionReferralModalAtom);
  const jpIdToPutOffline = useAtomValue(jobPositionIdToPutOfflineAtom);

  // Fetch JP details to get ppu_paid_until when the confirmation modal is open
  const { data: jpDetailsData } = useQuery({
    queryKey: [QUERY_KEYS.JOB_POSITIONS.DETAILS, jpIdToPutOffline, 'ppu_paid_until'],
    queryFn: () => jobPositionsService.showJobPosition(jpIdToPutOffline as string),
    enabled: !!jpIdToPutOffline && isConfirmationModalOpen,
    staleTime: 60 * 1000,
  });

  const ppuPaidUntil = jpDetailsData?.data?.ppu_paid_until ?? null;

  // Fetch subscription feedback status when confirmation modal is open
  useFetchIsSubscriptionFeedbacksSubmittedQuery();

  const isSubscriptionFeedbacksSubmitted = useIsSubscriptionFeedbacksSubmitted();

  // Put JP offline mutation — supports optional delay

  const { mutate: putOfflineJobPosition, isPending: isPuttingOffline } = useMutation({
    mutationFn: ({ jobPositionId, delaySeconds }: { jobPositionId: string; delaySeconds?: number }) =>
      jobPositionsService.putOfflineJobPosition(jobPositionId, delaySeconds),
    onSuccess: (_data, variables) => {
      trackEvent('employer_jp_goes_offline', {
        jp_id: variables.jobPositionId,
        organization_id: user?.organization_id,
        had_nps_delay: !!variables.delaySeconds,
      });
      if (!variables.delaySeconds) {
        // Immediate offline — invalidate queries to refresh JP status
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.JOB_POSITIONS.LIST],
        });
      }
      setIsConfirmationModalOpen(false);
    },
    onError: () => {
      showToast(t('put_offline_error_message'), 'error', {
        position: 'top-right',
        autoClose: 3000,
      });
      setIsConfirmationModalOpen(false);
      setIsNPSModalOpen(false);
    },
  });

  // Submit NPS feedback mutation
  const { mutate: submitNPSFeedback, isPending: isSubmittingFeedback } = useMutation({
    mutationFn: async (feedbackData: NPSFeedbackData) => {
      return employersService.createEmployerSubscriptionFeedback({
        organization_id: user?.organization_id,
        service_referral_rating: feedbackData.npsScore.toString(),
        motive_for_cancelling_subscription: feedbackData.motiveForCancelling,
        additional_feedback: feedbackData.additionalFeedback,
        reactivate_service: feedbackData.reactivationDate,
      });
    },
    onSuccess: (_data, variables) => {
      // NPS submitted — now put JP offline immediately (cancels the scheduled delayed job)
      if (jpIdToPutOffline) {
        putOfflineJobPosition({ jobPositionId: jpIdToPutOffline });
      }
      setIsNPSModalOpen(false);

      // Branch by score: Recovery (1-6), Referral (9-10), or standard success (7-8).
      if (variables.npsScore <= NPS_MOTIVE_MAX) {
        setIsRecoveryModalOpen(true);
      } else if (variables.npsScore >= NPS_REFERRAL_MIN) {
        setIsReferralModalOpen(true);
      } else {
        setIsSuccessModalOpen(true);
      }
    },
    onError: () => {
      showToast(t('nps_feedback_error_message'), 'error', {
        position: 'top-right',
        autoClose: 3000,
      });
    },
  });

  const handleOpenPutOffline = useCallback(
    (jpId: string) => {
      setJpIdToPutOffline(jpId);
      setIsConfirmationModalOpen(true);
    },
    [setJpIdToPutOffline, setIsConfirmationModalOpen]
  );

  // Step 1: User confirms they want to put offline
  const handleConfirmPutOffline = useCallback(() => {
    if (!jpIdToPutOffline) return;

    if (isSubscriptionFeedbacksSubmitted) {
      // NPS already submitted recently — put offline immediately, no delay needed
      putOfflineJobPosition({ jobPositionId: jpIdToPutOffline });
      setIsSuccessModalOpen(true);
    } else {
      // Schedule delayed offline (60s safety net) and show NPS modal
      putOfflineJobPosition({
        jobPositionId: jpIdToPutOffline,
        delaySeconds: NPS_OFFLINE_DELAY_SECONDS,
      });
      setIsConfirmationModalOpen(false);
      setIsNPSModalOpen(true);
    }
  }, [
    isSubscriptionFeedbacksSubmitted,
    jpIdToPutOffline,
    putOfflineJobPosition,
    setIsConfirmationModalOpen,
    setIsNPSModalOpen,
    setIsSuccessModalOpen,
  ]);

  // Step 2: User submits NPS feedback
  const handleSubmitNPSFeedback = useCallback(
    (feedbackData: NPSFeedbackData) => {
      submitNPSFeedback(feedbackData);
    },
    [submitNPSFeedback]
  );

  const handleCloseConfirmationModal = useCallback(() => {
    if (!isPuttingOffline) {
      setIsConfirmationModalOpen(false);
      setJpIdToPutOffline(null);
    }
  }, [isPuttingOffline, setIsConfirmationModalOpen, setJpIdToPutOffline]);

  const handleCloseNPSModal = useCallback(() => {
    if (!isSubmittingFeedback) {
      // JP is NOT offline yet (delayed job scheduled) — user goes back to list
      // They'll see JP still online, incentivizing them to come back and complete NPS
      setIsNPSModalOpen(false);
      setJpIdToPutOffline(null);
    }
  }, [isSubmittingFeedback, setIsNPSModalOpen, setJpIdToPutOffline]);

  const handleCloseSuccessModal = useCallback(() => {
    const jobPositionId = jpIdToPutOffline;
    setIsSuccessModalOpen(false);
    setJpIdToPutOffline(null);
    if (jobPositionId) {
      void queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.JOB_POSITIONS.DETAILS, jobPositionId],
        exact: true,
      });
    }
  }, [jpIdToPutOffline, queryClient, setIsSuccessModalOpen, setJpIdToPutOffline]);

  const handleCloseRecoveryModal = useCallback(() => {
    const jobPositionId = jpIdToPutOffline;
    setIsRecoveryModalOpen(false);
    setJpIdToPutOffline(null);
    if (jobPositionId) {
      void queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.JOB_POSITIONS.DETAILS, jobPositionId],
        exact: true,
      });
    }
  }, [jpIdToPutOffline, queryClient, setIsRecoveryModalOpen, setJpIdToPutOffline]);

  const handleCloseReferralModal = useCallback(() => {
    const jobPositionId = jpIdToPutOffline;
    setIsReferralModalOpen(false);
    setJpIdToPutOffline(null);
    if (jobPositionId) {
      void queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.JOB_POSITIONS.DETAILS, jobPositionId],
        exact: true,
      });
    }
  }, [jpIdToPutOffline, queryClient, setIsReferralModalOpen, setJpIdToPutOffline]);

  return {
    isPuttingOffline,
    isSubmittingFeedback,
    isConfirmationModalOpen,
    isNPSModalOpen,
    isSuccessModalOpen,
    isRecoveryModalOpen,
    isReferralModalOpen,
    jpIdToPutOffline,
    ppuPaidUntil,
    isSubscriptionFeedbacksSubmitted,
    handleOpenPutOffline,
    handleConfirmPutOffline,
    handleSubmitNPSFeedback,
    handleCloseConfirmationModal,
    handleCloseNPSModal,
    handleCloseSuccessModal,
    handleCloseRecoveryModal,
    handleCloseReferralModal,
  };
}
